/** @format */

import {format, subDays} from 'date-fns';

import {safeDivide} from 'helpers/numberHelpers';

import {GraphComplexValuesList} from '../../../models/common';

export interface BasicCrStats {
  yesterday: number;
  min: number;
  max: number;
  dynamics: number;
}

const computeBasicCrStats = (
  graphComplexValues: GraphComplexValuesList,
  numeratorKey: 'clicks' | 'impressions' | 'shows',
  denominatorKey: 'clicks' | 'impressions' | 'shows',
): BasicCrStats => {
  const pointNames = Object.keys(graphComplexValues).sort();
  const crSeries: number[] = pointNames.map(name => {
    const data = graphComplexValues[name];
    if (!data) return Number.NaN;
    const normalize = (k: 'clicks' | 'impressions' | 'shows') =>
      k === 'shows' ? 'impressions' : k;
    const denominatorRaw = data.values[normalize(denominatorKey)];
    const numeratorRaw = data.values[normalize(numeratorKey)];

    // Исключаем из расчёта дни без данных или с нулевым знаменателем
    if (
      denominatorRaw === undefined ||
      denominatorRaw === null ||
      denominatorRaw <= 0 ||
      numeratorRaw === undefined ||
      numeratorRaw === null
    ) {
      return Number.NaN;
    }

    return safeDivide(numeratorRaw, denominatorRaw) * 100;
  });

  // Жёстко берём календарное «вчера» по дате из данных
  const yesterdayIso = format(subDays(new Date(), 1), 'yyyy-MM-dd');
  const yesterdayRaw = (() => {
    for (let i = pointNames.length - 1; i >= 0; i--) {
      const data = graphComplexValues[pointNames[i]];
      if (data?.date === yesterdayIso) {
        const normalize = (k: 'clicks' | 'impressions' | 'shows') =>
          k === 'shows' ? 'impressions' : k;
        const numerator = data.values[normalize(numeratorKey)] || 0;
        const denominator = data.values[normalize(denominatorKey)] || 0;
        return safeDivide(numerator, denominator) * 100;
      }
    }
    return Number.NaN;
  })();

  const valid = crSeries.filter(v => Number.isFinite(v));
  if (!valid.length) {
    // eslint-disable-next-line no-console
    console.warn('[CR] computeBasicCrStats: пустой ряд CR', {
      numeratorKey,
      denominatorKey,
      pointsCount: pointNames.length,
    });
  }
  const yesterday = Number.isFinite(yesterdayRaw) ? (yesterdayRaw as number) : 0;
  const min = valid.length ? Math.min(...valid) : 0;
  const max = valid.length ? Math.max(...valid) : 0;
  const first = valid.find(v => Number.isFinite(v)) || 0;
  const dynamics = yesterday - first;

  return {yesterday, min, max, dynamics};
};

export default computeBasicCrStats;
